import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../Api/baseQueryWithReauth';
import { TagTypes } from '../../../Api/Types/tagTypes';
import type {
  ApproveSupplierPrepaymentApContractorParams,
  ApproveSupplierPrepaymentApContractorResultDto,
  BankDocumentDto,
  BankDocumentEntriesQueueResultDto,
  BankDocumentEntryDto,
  GetBankDocumentEntriesQueueQuery,
  GetSupplierPrepaymentMovementsQuery,
  ImportBankDocumentCsvRequest,
  ImportBankDocumentResultDto,
  PostBankDocumentBatchPayload,
  PostBankDocumentBatchResultDto,
  PostBankDocumentEntryPayload,
  PostBankDocumentEntryResultDto,
  RegisterSupplierPrepaymentPayload,
  ReverseBankDocumentSupplierPrepaymentLinksPayload,
  ReverseBankDocumentSupplierPrepaymentLinksResultDto,
  CreateSupplierPrepaymentApContractorRequest,
  SupplierPrepaymentApContractorAdminDto,
  SupplierPrepaymentApContractorLookupDto,
  SupplierPrepaymentLedgerDto,
  SupplierPrepaymentMovementsResultDto,
  SupplierPrepaymentReconciliationDto,
  UnpostBankDocumentEntryPayload,
  UnpostBankDocumentEntryResultDto,
  UpdateBankDocumentEntryRequest,
} from '../Types/bankDocument.types';

let fallbackIdempotencyCounter = 0;

const createIdempotencyKey = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const toHex = (value: number): string => value.toString(16).padStart(2, '0');
    const hex = Array.from(bytes, toHex);
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
      .slice(6, 8)
      .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }

  fallbackIdempotencyCounter += 1;
  const randomHex = Math.floor(Math.random() * 0xffffffff)
    .toString(16)
    .padStart(8, '0');
  const timestampHex = Date.now().toString(16).slice(-8).padStart(8, '0');
  const counterHex = `${timestampHex}${randomHex}${fallbackIdempotencyCounter.toString(16)}`
    .slice(-12)
    .padStart(12, '0');
  return `00000000-0000-4000-8000-${counterHex}`;
};

const normalizeIdempotencyKey = (idempotencyKey: string | undefined): string => {
  const normalized = (idempotencyKey ?? createIdempotencyKey()).trim();

  if (!normalized) {
    throw new Error('Idempotency key is required.');
  }

  return normalized;
};

export const bankDocumentsApi = createApi({
  reducerPath: 'bankDocumentsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [TagTypes.BankDocument],
  endpoints: (builder) => ({
    getBankDocuments: builder.query<BankDocumentDto[], void>({
      query: () => '/bank-documents',
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({ type: TagTypes.BankDocument as const, id: item.id })),
              { type: TagTypes.BankDocument, id: TagTypes.List },
            ]
          : [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    getBankDocumentById: builder.query<BankDocumentDto, number>({
      query: (bankDocumentId) => `/bank-documents/${bankDocumentId}`,
      providesTags: (result, error, bankDocumentId) => [
        { type: TagTypes.BankDocument, id: bankDocumentId },
      ],
    }),
    getBankDocumentEntriesQueue: builder.query<
      BankDocumentEntriesQueueResultDto,
      GetBankDocumentEntriesQueueQuery
    >({
      query: (request) => {
        const queryParams = new URLSearchParams();

        if (request.companyId) {
          queryParams.set('companyId', request.companyId.toString());
        }

        if (request.bankAccountId) {
          queryParams.set('bankAccountId', request.bankAccountId.toString());
        }

        if (request.dateFrom) {
          queryParams.set('dateFrom', request.dateFrom);
        }

        if (request.dateTo) {
          queryParams.set('dateTo', request.dateTo);
        }

        if (request.postedState) {
          queryParams.set('postedState', request.postedState);
        }

        if (request.targetType) {
          queryParams.set('targetType', request.targetType);
        }

        if (request.amountDirection) {
          queryParams.set('amountDirection', request.amountDirection);
        }

        if (request.withUnassignedRemainder) {
          queryParams.set('withUnassignedRemainder', 'true');
        }

        if (request.counterpartyName) {
          queryParams.set('counterpartyName', request.counterpartyName);
        }

        if (request.title) {
          queryParams.set('title', request.title);
        }

        queryParams.set('pageNumber', (request.pageNumber ?? 1).toString());
        queryParams.set('pageSize', (request.pageSize ?? 50).toString());

        return `/bank-documents/entries?${queryParams.toString()}`;
      },
      providesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    importBankDocumentCsv: builder.mutation<
      ImportBankDocumentResultDto,
      ImportBankDocumentCsvRequest
    >({
      query: ({ formData, accountNumber }) => ({
        url: accountNumber
          ? `/bank-documents/import-csv?accountNumber=${encodeURIComponent(accountNumber)}`
          : '/bank-documents/import-csv',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    postBankDocumentEntry: builder.mutation<
      PostBankDocumentEntryResultDto,
      PostBankDocumentEntryPayload
    >({
      query: (payload) => ({
        url: `/bank-documents/entries/${payload.entryId}/post?postedByUserId=${payload.postedByUserId}`,
        method: 'POST',
        headers: {
          'X-Idempotency-Key': normalizeIdempotencyKey(payload.idempotencyKey),
        },
      }),
      invalidatesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    postBankDocumentBatch: builder.mutation<
      PostBankDocumentBatchResultDto,
      PostBankDocumentBatchPayload
    >({
      query: ({ bankDocumentId, postedByUserId }) => ({
        url: `/bank-documents/${bankDocumentId}/post?postedByUserId=${postedByUserId}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, payload) => [
        { type: TagTypes.BankDocument, id: payload.bankDocumentId },
        { type: TagTypes.BankDocument, id: TagTypes.List },
      ],
    }),
    getBankDocumentPostingStatus: builder.query<PostBankDocumentBatchResultDto, number>({
      query: (bankDocumentId) => `/bank-documents/${bankDocumentId}/posting-status`,
      providesTags: (result, error, bankDocumentId) => [
        { type: TagTypes.BankDocument, id: bankDocumentId },
      ],
    }),
    unpostBankDocumentEntry: builder.mutation<UnpostBankDocumentEntryResultDto, UnpostBankDocumentEntryPayload>({
      query: ({ entryId, idempotencyKey }) => ({
        url: `/bank-documents/entries/${entryId}/unpost`,
        method: 'POST',
        headers: {
          'X-Idempotency-Key': normalizeIdempotencyKey(idempotencyKey),
        },
      }),
      invalidatesTags: (result, error, payload) => [
        { type: TagTypes.BankDocument, id: payload.bankDocumentId },
        { type: TagTypes.BankDocument, id: TagTypes.List },
      ],
    }),
    updateBankDocumentEntry: builder.mutation<BankDocumentEntryDto, UpdateBankDocumentEntryRequest>(
      {
        query: ({ entryId, bankDocumentId: _bankDocumentId, ...body }) => ({
          url: `/bank-documents/entries/${entryId}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: (result, error, payload) => [
          { type: TagTypes.BankDocument, id: payload.bankDocumentId },
          { type: TagTypes.BankDocument, id: TagTypes.List },
        ],
      }
    ),
    registerSupplierPrepayment: builder.mutation<void, RegisterSupplierPrepaymentPayload>({
      query: ({ entryId, createdByUserId, supplierApContractorId, description }) => {
        const queryParams = new URLSearchParams();
        queryParams.set('createdByUserId', createdByUserId.toString());
        queryParams.set('supplierApContractorId', supplierApContractorId.toString());

        if (description?.trim()) {
          queryParams.set('description', description.trim());
        }

        return {
          url: `/bank-documents/entries/${entryId}/register-supplier-prepayment?${queryParams.toString()}`,
          method: 'POST',
        };
      },
      invalidatesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    reverseSupplierPrepaymentLinks: builder.mutation<
      ReverseBankDocumentSupplierPrepaymentLinksResultDto,
      ReverseBankDocumentSupplierPrepaymentLinksPayload
    >({
      query: ({ bankDocumentId, createdByUserId, description }) => {
        const queryParams = new URLSearchParams();
        queryParams.set('createdByUserId', createdByUserId.toString());

        if (description?.trim()) {
          queryParams.set('description', description.trim());
        }

        return {
          url: `/bank-documents/${bankDocumentId}/reverse-supplier-prepayment-links?${queryParams.toString()}`,
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, payload) => [
        { type: TagTypes.BankDocument, id: payload.bankDocumentId },
        { type: TagTypes.BankDocument, id: TagTypes.List },
      ],
    }),
    deleteBankDocument: builder.mutation<void, number>({
      query: (bankDocumentId) => ({
        url: `/bank-documents/${bankDocumentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    getSupplierPrepaymentLedger: builder.query<
      SupplierPrepaymentLedgerDto,
      { apContractorId: number; currency: string }
    >({
      query: ({ apContractorId, currency }) =>
        `/supplier-prepayments/ledger?apContractorId=${apContractorId}&currency=${encodeURIComponent(currency)}`,
      providesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    getSupplierPrepaymentMovements: builder.query<
      SupplierPrepaymentMovementsResultDto,
      GetSupplierPrepaymentMovementsQuery
    >({
      query: ({ apContractorId, currency, pageNumber = 1, pageSize = 50 }) =>
        `/supplier-prepayments/movements?apContractorId=${apContractorId}&currency=${encodeURIComponent(
          currency
        )}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
      providesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    getSupplierPrepaymentApContractorsLookup: builder.query<
      SupplierPrepaymentApContractorLookupDto[],
      string | undefined
    >({
      query: (search) =>
        search?.trim()
          ? `/supplier-prepayments/ap-contractors/lookup?search=${encodeURIComponent(search.trim())}`
          : '/supplier-prepayments/ap-contractors/lookup',
      providesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    getSupplierPrepaymentApContractorsAdminList: builder.query<
      SupplierPrepaymentApContractorAdminDto[],
      void
    >({
      query: () => '/supplier-prepayments/ap-contractors',
      providesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    createSupplierPrepaymentApContractor: builder.mutation<
      SupplierPrepaymentApContractorAdminDto,
      CreateSupplierPrepaymentApContractorRequest
    >({
      query: (body) => ({
        url: '/supplier-prepayments/ap-contractors',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    approveSupplierPrepaymentApContractor: builder.mutation<
      ApproveSupplierPrepaymentApContractorResultDto,
      ApproveSupplierPrepaymentApContractorParams
    >({
      query: ({ apContractorId }) => ({
        url: `/supplier-prepayments/ap-contractors/${apContractorId}/approve`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    deleteSupplierPrepaymentApContractor: builder.mutation<void, number>({
      query: (apContractorId) => ({
        url: `/supplier-prepayments/ap-contractors/${apContractorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
    getSupplierPrepaymentReconciliation: builder.query<SupplierPrepaymentReconciliationDto, number>(
      {
        query: (apContractorId) =>
          `/supplier-prepayments/reconciliation?apContractorId=${apContractorId}`,
        providesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
      }
    ),
  }),
});

export const {
  useGetBankDocumentsQuery,
  useGetBankDocumentByIdQuery,
  useLazyGetBankDocumentByIdQuery,
  useGetBankDocumentEntriesQueueQuery,
  useImportBankDocumentCsvMutation,
  usePostBankDocumentEntryMutation,
  usePostBankDocumentBatchMutation,
  useLazyGetBankDocumentPostingStatusQuery,
  useUnpostBankDocumentEntryMutation,
  useUpdateBankDocumentEntryMutation,
  useRegisterSupplierPrepaymentMutation,
  useReverseSupplierPrepaymentLinksMutation,
  useDeleteBankDocumentMutation,
  useGetSupplierPrepaymentLedgerQuery,
  useGetSupplierPrepaymentMovementsQuery,
  useGetSupplierPrepaymentApContractorsLookupQuery,
  useGetSupplierPrepaymentApContractorsAdminListQuery,
  useCreateSupplierPrepaymentApContractorMutation,
  useApproveSupplierPrepaymentApContractorMutation,
  useDeleteSupplierPrepaymentApContractorMutation,
  useGetSupplierPrepaymentReconciliationQuery,
} = bankDocumentsApi;
